// Port of ios/Sources/ViewModels/ChannelStep.swift.
//
// ⌥↑ / ⌥↓ — "the conversation above / below the one I am in", the shortcut every desktop chat
// client has and the reason a reader can work a morning's unread without touching the mouse
// (Mattermost Desktop binds the same two keys).
//
// A PURE rule, deliberately: what "the next conversation" means is the whole feature, and it is
// not obvious. It is NOT the store's channel list and NOT the category's rows — it is exactly the
// sequence of rows the sidebar is DRAWING at that moment, which is the shared derivation's
// sections, each one already through `ChannelListDerivation.visibleRows`. That matters because
// the drawn list is narrower than the data in three ways the keyboard must respect:
//
// - a collapsed category shows only what escapes it (unread/mentioned, never muted);
// - the lens ("No leídos", "Favoritos") and the filter field have already re-written the sections
//   by the time they arrive here — stepping through a filtered sidebar must stay inside the
//   filter, or the key disagrees with the eye;
// - the OPEN conversation is surfaced by `visibleRows` even inside a collapsed category, so the
//   step always has a place to start from.
//
// Desktop-only on purpose, with no Kotlin twin: Android has no second key to hold.
import { visibleRows } from './channelListDerivation';
import type { ChannelListSection } from './channelListDisplayModels';

export type Direction = 'previous' | 'next';

function delta(direction: Direction): number {
  return direction === 'next' ? 1 : -1;
}

/**
 * The drawn rows, flattened across categories — the sidebar's own order, headers removed.
 *
 * Deduplicated by id, keeping the first appearance. A channel cannot legitimately sit in two
 * categories, but if one ever did, the second copy would be a TRAP rather than a duplicate:
 * the walk below finds a selection by its first index, so landing on the second copy would
 * make the next key jump back to the first one and the reader could never get past it.
 */
export function orderedIds(
  sections: readonly ChannelListSection[],
  isSearching: boolean,
  selectedId: string | null | undefined,
): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const section of sections) {
    const rows = visibleRows(section.rows, { collapsed: section.collapsed, isSearching, selectedId });
    for (const row of rows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      ordered.push(row.id);
    }
  }
  return ordered;
}

/**
 * Where ⌥↑/⌥↓ should land, or `null` when the key has nothing to do — an empty sidebar, or a
 * single conversation, where re-selecting what is already open would re-run the open (and its
 * mark-as-read) for no movement.
 *
 * WRAPS at both ends, the webapp's behaviour: a shortcut that dies silently at the last row
 * reads as broken, and the list is a ring the reader is cycling, not a document they are
 * scrolling. With nothing open — the empty detail pane, or a selection that belongs to the
 * team they just left — ⌥↓ starts at the top and ⌥↑ at the bottom, so the keys work before
 * the first click of a session.
 */
export function target(
  sections: readonly ChannelListSection[],
  isSearching: boolean,
  selectedId: string | null | undefined,
  direction: Direction,
): string | null {
  const ordered = orderedIds(sections, isSearching, selectedId);
  if (ordered.length === 0) return null;
  const index = selectedId == null ? -1 : ordered.indexOf(selectedId);
  if (index < 0) {
    return direction === 'next' ? ordered[0] : ordered[ordered.length - 1];
  }
  const next = ordered[(index + delta(direction) + ordered.length) % ordered.length];
  return next === selectedId ? null : next;
}

/** Namespace form, for `ChannelStep.target(...)` call sites. */
export const ChannelStep = { orderedIds, target } as const;
